import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { IProperty } from "./types"

import {
    SegmentsAdminService,
    avy_api_pkg_segment_SegmentType,
    segment_SearchRequest,
} from "src/api"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { reportError } from "src/lib/report"

export class PropertiesStore {
    //#region initialization
    static Context = React.createContext<PropertiesStore | null>(null)
    static LoadingKeys = createLoadingKeys("loading")
    properties = new Pagination(
        async (params) => {
            const request: segment_SearchRequest = {
                page_number: params.page,
                page_size: params.pageSize,
                text_search: params.search ?? undefined,
                segment_types: this._segmentType ?? undefined,
            }

            // Only include access_group_id if it's not the default (-1)
            if (this.accessGroupId !== -1) {
                request.access_group_id = this.accessGroupId
            }

            const response = await SegmentsAdminService.segmentSearchPost({
                request,
            })

            const items: IProperty[] = (response.segments ?? []).map(
                (item) => ({
                    id: item.id ?? -1,
                    name: item.name ?? "",
                    ancestors: item.ancestors,
                    has_selected_ancestor: item.has_selected_ancestor,
                    is_selected: item.is_selected,
                    path: item.path,
                    tenant_count: item.tenant_count,
                    type: item.type,
                }),
            )

            return {
                items,
                count: response.total_count ?? 0,
            }
        },
        {
            loadingKey: PropertiesStore.LoadingKeys.loading,
        },
    )
    private repositoryUpdatesListenerDisposer?: () => void

    constructor() {
        makeAutoObservable(this)
    }

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    _segmentType: avy_api_pkg_segment_SegmentType | null = "property"

    private get accessGroupId() {
        return this._accessGroupId
    }

    get segmentType() {
        return this._segmentType
    }

    //#region store operations
    async init(accessGroupId: number) {
        this.listenToRepositoryUpdates()

        this.setAccessGroup(accessGroupId)
        try {
            await this.properties.loadInitialPage()
        } catch (error) {
            reportError(t`properties.init-fail`, error)
        }
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }

    async setSegmentType(segmentType: avy_api_pkg_segment_SegmentType | null) {
        this._segmentType = segmentType
        await this.properties.loadInitialPage()
    }

    //#region setters
    private setAccessGroup(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private listenToRepositoryUpdates() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "properties"
                ) {
                    await this.properties.reload()
                }
            },
        )
    }
    //#endregion
}
